import { ButtonGroup, cssVar, Flex, IconNameT, Box, IconButton, Text } from '@cad-arc';
import React, { useState, useEffect } from 'react';
import { Tooltip } from '../../common/components/tooltip';

export type ToggleOption = {
    id: string;
    label: string;
    tooltip?: string;
    icon?: IconNameT;
    testId?: string;
};

interface ToggleGroupProps {
    options: ToggleOption[];
    disabledOptions: ToggleOption['id'][];
    value?: ToggleOption;
    onItemChanged?: (option: ToggleOption) => void;
    useNewStyles?: boolean;
}

export const ToggleGroup: React.FC<ToggleGroupProps> = ({
    options,
    onItemChanged,
    disabledOptions,
    value,
    useNewStyles,
}) => {
    const [selectedOption, setSelectedOption] = useState<ToggleOption | undefined>();

    useEffect(() => {
        if (value && options.find((option) => option.id === value.id)) {
            setSelectedOption(value);
        } else {
            setSelectedOption(options?.[0]);
        }
    }, [options, value]);

    function onToggleItem(item: ToggleOption): void {
        setSelectedOption(item);
        onItemChanged?.(item);
    }

    if (useNewStyles) {
        return (
            <ButtonGroup isAttached>
                {options.map((option) => {
                    const isSelectedItem = option.id === selectedOption?.id;
                    const disabled = disabledOptions.includes(option.id);

                    if (option.icon) {
                        return (
                            <Tooltip key={option.label} overlay={option.tooltip}>
                                <IconButton
                                    disabled={disabled}
                                    onClick={() => {
                                        if (!disabled) {
                                            onToggleItem(option);
                                        }
                                    }}
                                    style={{
                                        backgroundColor: isSelectedItem
                                            ? 'var(--arc-colors-interactive-hover)'
                                            : undefined,
                                    }}
                                    aria-label={`Toggle ${option.label}`}
                                    icon={option.icon}
                                    data-testid={option.testId}
                                />
                            </Tooltip>
                        );
                    }
                })}
            </ButtonGroup>
        );
    }

    return (
        <Flex borderRadius="4px" overflow="hidden">
            {options.map((option) => {
                const isSelectedItem = option.id === selectedOption?.id;
                const disabled = disabledOptions.includes(option.id);
                return (
                    <Box
                        cursor={disabled ? 'not-allowed' : 'pointer'}
                        role="button"
                        flexGrow={1}
                        paddingX="16px"
                        paddingY="1px"
                        onClick={() => {
                            if (!disabled) {
                                onToggleItem(option);
                            }
                        }}
                        key={option.id}
                        bg={isSelectedItem ? 'brand.default' : 'brand.accent'}
                    >
                        <Text
                            variant="caption"
                            fontWeight="bold"
                            style={{
                                color:
                                    disabled && !isSelectedItem
                                        ? cssVar('arc.colors.text.placeholder')
                                        : isSelectedItem
                                        ? cssVar('arc.colors.brand.content')
                                        : cssVar('arc.colors.brand.accentContent'),
                            }}
                        >
                            {option.label}
                        </Text>
                    </Box>
                );
            })}
        </Flex>
    );
};
